import { useCallback, useEffect, useMemo } from 'react';

import * as Dfns from 'date-fns/fp';
import * as R from 'ramda';

import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useAppSelector } from '../../redux';
import { useSetError } from '../../redux/modals';
import * as UserRedux from '../../redux/user';

import { validDate } from '../../utils/date-utils';

import { BPMTable, Header } from '../../Components/StickyTable';
import { Button } from '../../Components/Button';
import { ChartContainer } from '../../Components/ChartContainer/ChartContainer';
import { EntityKind, EntityLogo, LogoVariant } from '../../Components/EntityLogo';
import { Page } from '../../Components/Page';
import { TableEmptyState } from '../../Components/TableEmptyState';
import { Size } from '../../Components/designConstants';

import { startDag } from './companies-utils';
import {
  INGEST_STATUSES_QUERY_KEY,
  useBuildStatusMap,
  useCompanyList,
  useIngestStatuses
} from './companies-data';
import {
  CompanyInfo,
  IngestStatusMap,
  IngestionButton,
  INGESTION_BUTTON_PROPS,
  KpiTableRow
} from './companies-constants';

import './Companies.scss';

const BUILD_HEADERS: Header[] = [
  { label: 'Company', name: 'company', width: 150, renderer: (data: KpiTableRow) => data.company },
  { label: 'ID', name: 'id', flex: 1, minFlexWidth: 200, renderer: (data: KpiTableRow) => data.id },
  {
    label: 'Build',
    name: 'batch_job_name',
    flex: 3,
    minFlexWidth: 300,
    renderer: (data: KpiTableRow) => (
      <a href={data.batch_log_url} target='_blank' rel='noopener noreferrer'>
        {data.batch_job_name}
      </a>
    )
  },
  {
    label: 'Status',
    name: 'status_batch',
    width: 80,
    renderer: (data: KpiTableRow) => data.status_batch
  },
  {
    label: 'Expected Completion (Local)',
    name: 'lastmodified',
    width: 260,
    renderer: (data: KpiTableRow) => {
      if (
        data.status_batch === 'SUCCEEDED' ||
        data.status_batch === 'FAILED' ||
        !validDate(new Date(data.now))
      ) {
        return '-';
      }
      return Dfns.format(
        'yyyy-MM-dd hh:mma',
        Dfns.addSeconds(data.total_expected_runtime, new Date(data.now))
      );
    }
  }
];

const noRows = () => <TableEmptyState />;

export const Companies = () => {
  const setError = useSetError();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isInternal = useAppSelector(UserRedux.isInternalSelector);
  const email = useAppSelector(UserRedux.emailSelector);

  const { data: companyList, isLoading, error: companyListError } = useCompanyList(email);
  const {
    data: buildStatusMap,
    isLoading: isBuildStatusLoading,
    error: buildStatusError
  } = useBuildStatusMap(isInternal);
  // Ingest-status errors are intentionally not surfaced — see useIngestStatuses.
  const { data: ingestStatuses } = useIngestStatuses(isInternal);

  const companyInfo = useMemo<CompanyInfo[]>(() => companyList ?? [], [companyList]);

  // Surface load failures through the shared error modal (React Query has no catch to hook).
  useEffect(() => {
    if (companyListError) {
      setError({
        message: `Failed to load company info: ${companyListError.message}`,
        reportError: companyListError
      });
    }
  }, [companyListError, setError]);

  useEffect(() => {
    if (buildStatusError) {
      setError({
        message: `Failed to load build status info: ${buildStatusError.message}`,
        reportError: buildStatusError
      });
    }
  }, [buildStatusError, setError]);

  // A single-company user has no list to browse — send them straight to their company (matching
  // the company-link cells), via the router rather than a full-page reload.
  useEffect(() => {
    if (companyList?.length === 1) {
      navigate(`/${companyList[0].cid}`, { replace: true });
    }
  }, [companyList, navigate]);

  const handleIngest = useCallback(
    (details: IngestionButton, cid: string) => {
      // Optimistically flip the button(s) to disabled in the cached ingest-status map.
      queryClient.setQueryData<IngestStatusMap>(INGEST_STATUSES_QUERY_KEY, prev => {
        if (!prev) {
          return prev;
        }
        const next: IngestStatusMap = { ...prev };
        for (const type of details.disableTypes) {
          if (next[cid]?.[type]) {
            next[cid] = { ...next[cid], [type]: false };
          }
        }
        return next;
      });
      startDag({ dag: cid, ingestionType: details.type, setError });
    },
    [queryClient, setError]
  );

  const leftTableHeaders = useMemo<Header[]>(() => {
    const headers: Header[] = [
      {
        label: 'Name',
        name: 'name',
        sortPriority: 0,
        sortAscending: true,
        flex: 1,
        renderer: (data: CompanyInfo) => <CompanyNameCell company={data} />
      }
    ];
    if (isInternal) {
      headers.push({
        label: 'Ingest Now',
        nonInteractive: true,
        width: 250,
        renderer: (data: CompanyInfo) => (
          <IngestButtons company={data} ingestStatuses={ingestStatuses} onIngest={handleIngest} />
        )
      });
    }
    return headers;
  }, [isInternal, ingestStatuses, handleIngest]);

  const nonMobiusKPIs = useMemo<KpiTableRow[] | null>(() => {
    if (!buildStatusMap) {
      return null;
    }
    if (Object.keys(buildStatusMap).length === 0) {
      return [];
    }
    return companyInfo.flatMap(company =>
      company.KPIs.flatMap(kpi =>
        (buildStatusMap[kpi.id] || []).map(build => ({ ...build, ...kpi, company: company.cid }))
      )
    );
  }, [buildStatusMap, companyInfo]);

  // These kpis don't have a company or specific kpi attached to them
  const mobiusKPIs = useMemo<KpiTableRow[] | null>(() => {
    if (!buildStatusMap) {
      return null;
    }
    if (Object.keys(buildStatusMap).length === 0) {
      return [];
    }
    return R.uniq(
      Object.values(buildStatusMap)
        .flat()
        .filter(build => build.batch_job_name.includes('MOBIUS'))
        .map(build => ({ ...build, kpi: 'N/A', company: 'N/A' }))
    );
  }, [buildStatusMap]);

  const kpis = useMemo<KpiTableRow[]>(
    () => [...(nonMobiusKPIs ?? []), ...(mobiusKPIs ?? [])],
    [mobiusKPIs, nonMobiusKPIs]
  );

  return (
    <Page title='Companies' pageType='Companies' minHeight='600px' isLoading={isLoading}>
      <div className='bpm-companies'>
        <div className='reconcile-body companies' data-testid='companies-table'>
          <ChartContainer title='Companies' isLoading={isLoading}>
            <BPMTable
              data={companyInfo}
              headers={leftTableHeaders}
              headerHeight={40}
              noRowsRenderer={noRows}
            />
          </ChartContainer>
        </div>
        <div className='reconcile-body builds' data-testid='builds-table'>
          <ChartContainer title='Build Status' isLoading={isBuildStatusLoading}>
            <BPMTable
              data={kpis}
              headers={BUILD_HEADERS}
              headerHeight={40}
              defaultAdvancedFilter
              defaultTokens={{ advanced: ['Status', 'is not like', 'SUCCEEDED'] }}
              noRowsRenderer={noRows}
            />
          </ChartContainer>
        </div>
      </div>
    </Page>
  );
};

const CompanyNameCell = ({ company }: { company: CompanyInfo }) => (
  <div className='companies-name-column'>
    <Link
      className='companies-logo'
      to={`/${company.cid}`}
      data-testid={`company-logo-${company.cid}`}
    >
      <EntityLogo
        kind={EntityKind.COMPANY}
        id={company.cid}
        name={company.name}
        size={Size.LG}
        variant={LogoVariant.WIDE}
      />
    </Link>
    <Link
      className='companies-link'
      to={`/${company.cid}`}
      data-testid={`company-link-${company.cid}`}
    >
      {company.name}
    </Link>
  </div>
);

interface IngestButtonsProps {
  company: CompanyInfo;
  ingestStatuses?: IngestStatusMap;
  onIngest: (details: IngestionButton, cid: string) => void;
}

const IngestButtons = ({ company, ingestStatuses, onIngest }: IngestButtonsProps) => (
  <div className='companies-ingest-buttons'>
    {INGESTION_BUTTON_PROPS.map(details => (
      <Button
        key={`${company.cid}_${details.type}`}
        size={Size.SM}
        data-testid={`ingest-button-${company.cid}-${details.type}`}
        disabled={!ingestStatuses?.[company.cid]?.[details.type]}
        onClick={() => onIngest(details, company.cid)}
      >
        {details.label}
      </Button>
    ))}
  </div>
);
